import MetaTags from "../components/MetaTags";
import React, { useState, useMemo } from "react";
import {
  Box,
  Heading,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Image,
  Flex,
  Input,
  HStack,
  Link,
  IconButton,
  Text,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Badge,
  Button,
  Stack,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import faqData from "../pages/data.json";
import { BsYoutube } from "react-icons/bs";
import Customerservicebot from "./customerservicesbot";
import { getFAQSchema } from "../config/seo";
import url from "../config/config.json";

const FaqAccordion = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [searchResults, setSearchResults] = useState({ general: [], candidates: [], companies: [] });
  const [activeTab, setActiveTab] = useState(0);

  // Deactivate states
  const [deactivateEmail, setDeactivateEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [deactivateStatus, setDeactivateStatus] = useState(null);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingAndDeactivating, setIsVerifyingAndDeactivating] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  // Delete states
  const [deleteEmail, setDeleteEmail] = useState("");
  const [deleteOtp, setDeleteOtp] = useState("");
  const [deleteStatus, setDeleteStatus] = useState(null);
  const [isSendingDeleteOtp, setIsSendingDeleteOtp] = useState(false);
  const [isVerifyingAndDeleting, setIsVerifyingAndDeleting] = useState(false);
  const [deleteOtpSent, setDeleteOtpSent] = useState(false);

  const handleAccordionClick = (index) => {
    setActiveIndex(index === activeIndex ? null : index);
  };

  const updateSearchResults = (keyword) => {
    const trimmed = keyword.trim();
    if (!trimmed) {
      setSearchResults({ general: [], candidates: [], companies: [] });
      return;
    }

    const lower = trimmed.toLowerCase();

    const generalResults = faqData.general.filter(
      (faq) =>
        faq.title.toLowerCase().includes(lower) ||
        faq.content.toLowerCase().includes(lower)
    );
    const candidateResults = faqData.candidates.filter(
      (faq) =>
        faq.title.toLowerCase().includes(lower) ||
        faq.content.toLowerCase().includes(lower)
    );
    const companyResults = faqData.companies.filter(
      (faq) =>
        faq.title.toLowerCase().includes(lower) ||
        faq.content.toLowerCase().includes(lower)
    );
    setSearchResults({ general: generalResults, candidates: candidateResults, companies: companyResults });
  };

  const handleSearch = () => {
    updateSearchResults(searchKeyword);
  };

  const handleClearSearch = () => {
    setSearchKeyword("");
    setSearchResults({ general: [], candidates: [], companies: [] });
  };

  const getCurrentData = (type) => {
    if (searchKeyword.trim()) return searchResults[type];
    return faqData[type];
  };

  // ─── Deactivate Handlers ───────────────────────────────────────────────────

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setDeactivateStatus(null);
    if (!deactivateEmail) {
      setDeactivateStatus({ type: "error", message: "Please enter the email linked to your account." });
      return;
    }
    try {
      setIsSendingOtp(true);
      const baseUrl = url.baseUrl;
      const response = await fetch(`${baseUrl}/send_otp_email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: deactivateEmail }),
      });
      const data = await response.json().catch(() => null);
      if (response.ok) {
        setOtpSent(true);
        setDeactivateStatus({ type: "success", message: data?.message || "OTP has been sent to your email." });
      } else {
        setOtpSent(false);
        setDeactivateStatus({
          type: "error",
          message:
            data?.error === "User not found"
              ? "No account found with this email address. Please check and try again."
              : data?.message || data?.error || "Unable to send OTP. Please try again.",
        });
      }
    } catch (error) {
      setDeactivateStatus({ type: "error", message: "Something went wrong. Please try again later." });
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyAndDeactivate = async (e) => {
    e.preventDefault();
    setDeactivateStatus(null);
    if (!deactivateEmail || !otp) {
      setDeactivateStatus({ type: "error", message: "Please enter both email and OTP." });
      return;
    }
    try {
      setIsVerifyingAndDeactivating(true);
      const baseUrl = url.baseUrl;
      const verifyResponse = await fetch(`${baseUrl}/verify_otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: deactivateEmail, otp }),
      });
      const verifyData = await verifyResponse.json().catch(() => null);
      if (!verifyResponse.ok || !verifyData?.success) {
        setDeactivateStatus({
          type: "error",
          message: verifyData?.message || verifyData?.error || "Invalid OTP. Please check and try again.",
        });
        return;
      }
      const deactivateResponse = await fetch(`${baseUrl}/deactivate_user_by_email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: deactivateEmail }),
      });
      const deactivateData = await deactivateResponse.json().catch(() => null);
      if (deactivateResponse.ok) {
        setDeactivateStatus({ type: "success", message: deactivateData?.message || "Account deactivated successfully." });
        setDeactivateEmail("");
        setOtp("");
        setOtpSent(false);
      } else {
        setDeactivateStatus({
          type: "error",
          message:
            deactivateData?.error === "User not found"
              ? "No account found with this email."
              : deactivateData?.message || deactivateData?.error || "Unable to deactivate the account. Please try again.",
        });
      }
    } catch (error) {
      setDeactivateStatus({ type: "error", message: "Something went wrong. Please try again later." });
    } finally {
      setIsVerifyingAndDeactivating(false);
    }
  };

  // ─── Delete Handlers ───────────────────────────────────────────────────────

  const handleSendDeleteOtp = async (e) => {
    e.preventDefault();
    setDeleteStatus(null);
    if (!deleteEmail) {
      setDeleteStatus({ type: "error", message: "Please enter the email linked to your account." });
      return;
    }
    try {
      setIsSendingDeleteOtp(true);
      const baseUrl = url.baseUrl;
      const response = await fetch(`${baseUrl}/send_otp_email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: deleteEmail }),
      });
      const data = await response.json().catch(() => null);
      if (response.ok) {
        setDeleteOtpSent(true);
        setDeleteStatus({ type: "success", message: data?.message || "OTP has been sent to your email." });
      } else {
        setDeleteOtpSent(false);
        setDeleteStatus({
          type: "error",
          message:
            data?.error === "User not found"
              ? "No account found with this email address. Please check and try again."
              : data?.message || data?.error || "Unable to send OTP. Please try again.",
        });
      }
    } catch (error) {
      setDeleteStatus({ type: "error", message: "Something went wrong. Please try again later." });
    } finally {
      setIsSendingDeleteOtp(false);
    }
  };

  const handleVerifyAndDelete = async (e) => {
    e.preventDefault();
    setDeleteStatus(null);
    if (!deleteEmail || !deleteOtp) {
      setDeleteStatus({ type: "error", message: "Please enter both email and OTP." });
      return;
    }
    try {
      setIsVerifyingAndDeleting(true);
      const baseUrl = url.baseUrl;
      const verifyResponse = await fetch(`${baseUrl}/verify_otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: deleteEmail, otp: deleteOtp }),
      });
      const verifyData = await verifyResponse.json().catch(() => null);
      if (!verifyResponse.ok || !verifyData?.success) {
        setDeleteStatus({
          type: "error",
          message: verifyData?.message || verifyData?.error || "Invalid OTP. Please check and try again.",
        });
        return;
      }
      const deleteResponse = await fetch(`${baseUrl}/delete_user_by_email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: deleteEmail }),
      });
      const deleteData = await deleteResponse.json().catch(() => null);
      if (deleteResponse.ok) {
        setDeleteStatus({ type: "success", message: deleteData?.message || "Account deleted successfully." });
        setDeleteEmail("");
        setDeleteOtp("");
        setDeleteOtpSent(false);
      } else {
        setDeleteStatus({
          type: "error",
          message:
            deleteData?.error === "User not found"
              ? "No account found with this email. It may have already been deleted."
              : deleteData?.message || deleteData?.error || "Unable to delete the account. Please try again.",
        });
      }
    } catch (error) {
      setDeleteStatus({ type: "error", message: "Something went wrong. Please try again later." });
    } finally {
      setIsVerifyingAndDeleting(false);
    }
  };

  // ─── FAQ Schema (AEO) ──────────────────────────────────────────────────────

  const cleanFaqAnswer = (faq) =>
    faq.content
      .replace(/\[here\]/g, "here")
      .replace(/\[link\]/g, faq.linkText || "here")
      .trim();

  const faqSchema = useMemo(() => {
    const allFAQs = [
      ...faqData.general.map((faq) => ({ question: faq.title, answer: cleanFaqAnswer(faq) })),
      ...faqData.candidates.map((faq) => ({ question: faq.title, answer: cleanFaqAnswer(faq) })),
      ...faqData.companies.map((faq) => ({ question: faq.title, answer: cleanFaqAnswer(faq) })),
    ];
    return getFAQSchema(allFAQs);
  }, []);

  const faqKeywords =
    "Aptofy FAQ, recruitment FAQ, AI hiring questions, candidate assessment answers, Aptofy interview process, recruitment solution FAQ, talent evaluation questions, hiring platform FAQ, candidate experience, employer brand, proctored assessment, Aptofy signup login, assessment creation, bulk hiring FAQ, AI hiring platform, talent assessment software, video interview software, AI recruitment software";

  // ─── Reusable FAQ content renderer ────────────────────────────────────────

  const renderFaqContent = (faq) => {
    // Optional SEO page links: content uses [link] + faq.link / faq.linkText
    if (faq.link && faq.content.includes("[link]")) {
      return (
        <>
          {faq.content.split("[link]").map((text, i, arr) => (
            <React.Fragment key={i}>
              {text}
              {i < arr.length - 1 && (
                <Link href={faq.link} color="blue.500" textDecoration="underline">
                  {faq.linkText || "here"}
                </Link>
              )}
            </React.Fragment>
          ))}
        </>
      );
    }

    // Existing password-reset helper link (unchanged)
    if (faq.content.includes("[here]")) {
      return (
        <>
          {faq.content.split("[here]").map((text, i, arr) => (
            <React.Fragment key={i}>
              {text}
              {i < arr.length - 1 && (
                <Link href="/ResetEmailPage" color="blue.500" textDecoration="underline">
                  here
                </Link>
              )}
            </React.Fragment>
          ))}
        </>
      );
    }
    return faq.content;
  };

  // ─── Reusable FAQ Accordion list ──────────────────────────────────────────

  const renderFaqList = (type, hoverColor, emptyLabel) => {
    const data = getCurrentData(type);
    if (data.length === 0) {
      return (
        <Text textAlign="center" color="gray.500" py="20px">
          {searchKeyword.trim() ? `No ${emptyLabel} FAQs found for your search.` : `No ${emptyLabel} FAQs available.`}
        </Text>
      );
    }
    return (
      <Accordion allowMultiple>
        {data.map((faq, index) => (
          <AccordionItem
            key={`${type}-${index}`}
            itemScope
            itemType="https://schema.org/Question"
            as="section"
            aria-labelledby={`faq-${type}-q-${index}`}
          >
            {({ isExpanded }) => (
              <>
                <AccordionButton
                  onClick={() => handleAccordionClick(`${type}-${index}`)}
                  _hover={{ bg: hoverColor }}
                >
                  <Box
                    id={`faq-${type}-q-${index}`}
                    flex="1"
                    textAlign="left"
                    fontSize="16px"
                    fontWeight="medium"
                    itemProp="name"
                  >
                    {faq.title}
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel
                  display={isExpanded || activeIndex === `${type}-${index}` ? "block" : "none"}
                  fontSize="15px"
                  lineHeight="1.6"
                  color="gray.700"
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Text itemProp="text">{renderFaqContent(faq)}</Text>
                </AccordionPanel>
              </>
            )}
          </AccordionItem>
        ))}

        {(type === "general" || type === "candidates") && (
          <>
            {/* Deactivate Account */}
            <AccordionItem>
              {({ isExpanded }) => (
                <>
                  <AccordionButton _hover={{ bg: "orange.50" }}>
                    <Box flex="1" textAlign="left" fontSize="16px" fontWeight="medium">
                      How can I deactivate my account?
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel fontSize="15px" lineHeight="1.6" color="gray.700" pb={4}>
                    <Text mb={4}>
                      To deactivate your Aptofy account, first request an OTP on your registered email address and
                      then verify it below. Once the OTP is verified, your account will be deactivated.
                    </Text>

                    {/* Step 1: Send OTP */}
                    <Box as="form" onSubmit={handleSendOtp}>
                      <Stack
                        spacing={4}
                        direction={{ base: "column", md: "row" }}
                        align={{ base: "stretch", md: "flex-end" }}
                      >
                        <FormControl isRequired>
                          <FormLabel fontSize="sm">Email address</FormLabel>
                          <Input
                            type="email"
                            placeholder="you@example.com"
                            value={deactivateEmail}
                            onChange={(e) => {
                              setDeactivateEmail(e.target.value);
                              setOtpSent(false);
                              setOtp("");
                              setDeactivateStatus(null);
                            }}
                          />
                        </FormControl>
                        <Button
                          type="submit"
                          colorScheme="blue"
                          isLoading={isSendingOtp}
                          loadingText="Sending OTP..."
                          px={{ base: 6, md: 8 }}
                          minW={{ base: "full", md: "auto" }}
                        >
                          Send OTP
                        </Button>
                      </Stack>
                    </Box>

                    {/* Step 2: Verify OTP */}
                    {otpSent && (
                      <Box as="form" onSubmit={handleVerifyAndDeactivate} mt={4}>
                        <Stack
                          spacing={4}
                          direction={{ base: "column", md: "row" }}
                          align={{ base: "stretch", md: "flex-end" }}
                        >
                          <FormControl isRequired>
                            <FormLabel fontSize="sm">OTP</FormLabel>
                            <Input
                              type="text"
                              placeholder="Enter OTP"
                              value={otp}
                              onChange={(e) => setOtp(e.target.value)}
                            />
                          </FormControl>
                          <Button
                            type="submit"
                            colorScheme="red"
                            isLoading={isVerifyingAndDeactivating}
                            loadingText="Verifying..."
                            px={{ base: 6, md: 8 }}
                            minW={{ base: "full", md: "auto" }}
                          >
                            Verify OTP & Deactivate
                          </Button>
                        </Stack>
                      </Box>
                    )}

                    {deactivateStatus && (
                      <Alert status={deactivateStatus.type} mt={4} borderRadius="md">
                        <AlertIcon />
                        {deactivateStatus.message}
                      </Alert>
                    )}
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>

            {/* Delete Account */}
            <AccordionItem>
              {({ isExpanded }) => (
                <>
                  <AccordionButton _hover={{ bg: "red.50" }}>
                    <Box flex="1" textAlign="left" fontSize="16px" fontWeight="medium">
                      How can I delete my account?
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel fontSize="15px" lineHeight="1.6" color="gray.700" pb={4}>
                    <Text mb={4}>
                      To permanently delete your Aptofy account, first request an OTP on your registered email
                      address and then verify it below. Once verified, your account will be permanently deleted and{" "}
                      <strong>cannot be recovered</strong>.
                    </Text>

                    {/* Step 1: Send OTP */}
                    <Box as="form" onSubmit={handleSendDeleteOtp}>
                      <Stack
                        spacing={4}
                        direction={{ base: "column", md: "row" }}
                        align={{ base: "stretch", md: "flex-end" }}
                      >
                        <FormControl isRequired>
                          <FormLabel fontSize="sm">Email address</FormLabel>
                          <Input
                            type="email"
                            placeholder="you@example.com"
                            value={deleteEmail}
                            onChange={(e) => {
                              setDeleteEmail(e.target.value);
                              setDeleteOtpSent(false);
                              setDeleteOtp("");
                              setDeleteStatus(null);
                            }}
                          />
                        </FormControl>
                        <Button
                          type="submit"
                          colorScheme="red"
                          variant="outline"
                          isLoading={isSendingDeleteOtp}
                          loadingText="Sending OTP..."
                          px={{ base: 6, md: 8 }}
                          minW={{ base: "full", md: "auto" }}
                        >
                          Send OTP
                        </Button>
                      </Stack>
                    </Box>

                    {/* Step 2: Verify OTP */}
                    {deleteOtpSent && (
                      <Box as="form" onSubmit={handleVerifyAndDelete} mt={4}>
                        <Stack
                          spacing={4}
                          direction={{ base: "column", md: "row" }}
                          align={{ base: "stretch", md: "flex-end" }}
                        >
                          <FormControl isRequired>
                            <FormLabel fontSize="sm">OTP</FormLabel>
                            <Input
                              type="text"
                              placeholder="Enter OTP"
                              value={deleteOtp}
                              onChange={(e) => setDeleteOtp(e.target.value)}
                            />
                          </FormControl>
                          <Button
                            type="submit"
                            colorScheme="red"
                            isLoading={isVerifyingAndDeleting}
                            loadingText="Verifying..."
                            px={{ base: 6, md: 8 }}
                            minW={{ base: "full", md: "auto" }}
                          >
                            Verify OTP & Delete Account
                          </Button>
                        </Stack>
                      </Box>
                    )}

                    {deleteStatus && (
                      <Alert status={deleteStatus.type} mt={4} borderRadius="md">
                        <AlertIcon />
                        {deleteStatus.message}
                      </Alert>
                    )}
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>

            {/* Tutorials */}
            <AccordionItem>
              {({ isExpanded }) => (
                <>
                  <AccordionButton _hover={{ bg: "gray.50" }}>
                    <Box flex="1" textAlign="left" fontSize="16px" fontWeight="medium">
                      <HStack spacing={2}>
                        <BsYoutube color="#FF0000" size="18px" />
                        <Text>Tutorials</Text>
                      </HStack>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel fontSize="15px" lineHeight="1.6" color="gray.700" pb={4}>
                    <HStack spacing={3} align="center">
                      <Text>Tutorials are available for more information on our YouTube channel.</Text>
                      <Link href="https://www.youtube.com/@Aptofy_You" isExternal>
                        <IconButton
                          aria-label="YouTube"
                          variant="ghost"
                          size="md"
                          isRound
                          _hover={{ bg: "red.50" }}
                          color="#FF0000"
                          icon={<BsYoutube size="24px" />}
                        />
                      </Link>
                    </HStack>
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>
          </>
        )}
      </Accordion>
    );
  };

  // ─── JSX ──────────────────────────────────────────────────────────────────

  return (
    <>
      <MetaTags
        title="Aptofy FAQ: Answers to Your Recruitment Solution Queries"
        description="Find answers to common questions about Aptofy's innovative recruitment solutions. FAQ covers AI-powered hiring, candidate assessment, proctored interviews, signup and login, assessment creation, bulk hiring, employer brand, and sustainable recruitment. Get visibility into Aptofy features and support."
        url="https://www.aptofy.com/Faq"
        keywords={faqKeywords}
        structuredData={faqSchema}
      />
      <Customerservicebot />

      <Box display="flex" justifyContent="center" mt="50px" px={{ base: "20px", md: "0" }}>
        <Box
          className="container"
          borderRadius="10px"
          boxShadow="0 0 10px rgba(0, 0, 0, 0.5)"
          display="flex"
          flexDirection={{ base: "column", md: "row" }}
          alignItems={{ base: "center", md: "flex-start" }}
          maxW="1200px"
        >
          {/* Image Section */}
          <Box
            boxSize={{ base: "200px", md: "250px" }}
            display="flex"
            justifyContent="center"
            alignItems="center"
            p={{ base: "10px", md: "20px" }}
            mb={{ base: "20px", md: "0" }}
          >
            <Image src="../assets/faq.png" alt="FAQ" objectFit="contain" />
          </Box>

          {/* Content Section */}
          <Box flex="1" p={{ base: "20px", md: "40px" }}>

            {/* Heading */}
            <Heading
              as="h1"
              className="title"
              fontSize={{ base: "2xl", md: "3xl" }}
              textAlign="center"
              color="#3B58D7"
            >
              The Aptofy FAQ
            </Heading>

            {/* Search Bar */}
            <Flex align="center" justify="center" mt="20px">
              <Input
                placeholder="Search FAQ"
                value={searchKeyword}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchKeyword(value);
                  updateSearchResults(value);
                }}
                w={{ base: "100%", md: "70%" }}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              />
              <IconButton
                aria-label="Search"
                icon={<SearchIcon />}
                onClick={handleSearch}
                ml="3"
                colorScheme="blue"
              />
            </Flex>

            {/* Search Results Info */}
            {searchKeyword.trim() && (
              <Flex justify="center" mt="10px">
                <Text fontSize="sm" color="gray.600">
                  Found{" "}
                  {searchResults.general.length + searchResults.candidates.length + searchResults.companies.length}{" "}
                  results
                  {searchResults.general.length + searchResults.candidates.length + searchResults.companies.length > 0 && (
                    <Text
                      as="span"
                      color="blue.500"
                      cursor="pointer"
                      ml="10px"
                      onClick={handleClearSearch}
                      textDecoration="underline"
                    >
                      Clear search
                    </Text>
                  )}
                </Text>
              </Flex>
            )}

            {/* FAQ Tabs */}
            <Box className="accordion" mt="35px">
              <Tabs index={activeTab} onChange={setActiveTab} variant="enclosed" colorScheme="blue">
                <TabList justifyContent="center">
                  <Tab>
                    General Questions
                    <Badge ml="2" colorScheme="purple" variant="solid">
                      {getCurrentData("general").length}
                    </Badge>
                  </Tab>
                  <Tab>
                    For Candidates
                    <Badge ml="2" colorScheme="blue" variant="solid">
                      {getCurrentData("candidates").length}
                    </Badge>
                  </Tab>
                  <Tab>
                    For Companies
                    <Badge ml="2" colorScheme="green" variant="solid">
                      {getCurrentData("companies").length}
                    </Badge>
                  </Tab>
                </TabList>

                <TabPanels>
                  <TabPanel px="0">{renderFaqList("general", "purple.50", "general")}</TabPanel>
                  <TabPanel px="0">{renderFaqList("candidates", "blue.50", "candidate")}</TabPanel>
                  <TabPanel px="0">{renderFaqList("companies", "green.50", "company")}</TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default FaqAccordion;